import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Edit, Trash2, Lock, Users, Shield, Search } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { nombre: "", descripcion: "", permissions: [] };

export default function RolesPage() {
  const { token } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    const [r1, r2] = await Promise.all([
      axios.get(`${API}/roles`, auth),
      axios.get(`${API}/roles/modulos`, auth),
    ]);
    setRoles(r1.data);
    setCatalog(r2.data);
  };
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const filtered = roles.filter(r => !search.trim() || r.nombre.toLowerCase().includes(search.toLowerCase()));

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ nombre: r.nombre, descripcion: r.descripcion || "", permissions: r.permissions || [] });
    setOpen(true);
  };

  // Helpers para matriz permisos
  const hasAction = (mod, act) => {
    const entry = form.permissions.find(p => p.module === mod);
    return !!(entry && entry.actions.includes(act));
  };
  const toggleAction = (mod, act) => {
    setForm(f => {
      const perms = [...(f.permissions || [])];
      const idx = perms.findIndex(p => p.module === mod);
      if (idx === -1) return { ...f, permissions: [...perms, { module: mod, actions: [act] }] };
      const actions = perms[idx].actions.includes(act)
        ? perms[idx].actions.filter(a => a !== act)
        : [...perms[idx].actions, act];
      perms[idx] = { ...perms[idx], actions };
      // limpiar el módulo si no queda ninguna acción
      return { ...f, permissions: perms.filter(p => p.actions.length > 0) };
    });
  };
  const toggleModule = (mod, allActions) => {
    setForm(f => {
      const entry = f.permissions.find(p => p.module === mod);
      const isFull = entry && allActions.every(a => entry.actions.includes(a));
      const perms = f.permissions.filter(p => p.module !== mod);
      if (!isFull) perms.push({ module: mod, actions: [...allActions] });
      return { ...f, permissions: perms };
    });
  };

  const save = async () => {
    if (!form.nombre.trim()) { toast.error("El nombre del rol es obligatorio"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/roles/${editing.id}`, form, auth);
        toast.success(`Rol "${form.nombre}" actualizado`);
      } else {
        await axios.post(`${API}/roles`, form, auth);
        toast.success(`Rol "${form.nombre}" creado`);
      }
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar");
    }
  };

  const del = async (r) => {
    if (!window.confirm(`¿Eliminar el rol "${r.nombre}"?`)) return;
    try {
      await axios.delete(`${API}/roles/${r.id}`, auth);
      toast.success("Rol eliminado"); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const totalPerms = (r) => (r.permissions || []).reduce((s, p) => s + p.actions.length, 0);

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="roles-page">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="font-display font-black text-4xl text-green-900 flex items-center gap-2">
            <Shield className="w-8 h-8 text-orange-500" /> Roles y Permisos
          </h1>
          <p className="text-slate-600">Defina los perfiles de acceso a cada módulo y acción del sistema.</p>
        </div>
        <Button onClick={openNew} data-testid="btn-nuevo-rol" className="bg-orange-500 hover:bg-orange-600 text-white">
          <Plus className="w-4 h-4 mr-1" /> Nuevo Rol
        </Button>
      </div>

      <Card className="p-4 border-green-100">
        <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2 mb-4">
          <Search className="w-4 h-4 text-green-700" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar rol…" className="bg-transparent border-none focus-visible:ring-0 h-8" data-testid="input-search-rol" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-50 text-green-900"><tr>
              <th className="text-left p-2.5">Rol</th>
              <th className="text-left p-2.5">Descripción</th>
              <th className="text-center p-2.5">Permisos</th>
              <th className="text-center p-2.5">Usuarios</th>
              <th className="text-center p-2.5">Estado</th>
              <th className="text-right p-2.5">Acciones</th>
            </tr></thead>
            <tbody>{filtered.map(r => (
              <tr key={r.id} className="border-b border-green-50 hover:bg-green-50/40" data-testid={`row-rol-${r.id}`}>
                <td className="p-2.5 font-medium flex items-center gap-2">
                  {r.is_system && <Lock className="w-3.5 h-3.5 text-slate-400" title="Rol del sistema" />}
                  {r.nombre}
                </td>
                <td className="p-2.5 text-slate-600 max-w-md truncate" title={r.descripcion}>{r.descripcion || "-"}</td>
                <td className="p-2.5 text-center"><Badge className="bg-green-100 text-green-800 hover:bg-green-100">{totalPerms(r)}</Badge></td>
                <td className="p-2.5 text-center">
                  <span className="inline-flex items-center gap-1 text-slate-700"><Users className="w-3.5 h-3.5" /> {r.users_count}</span>
                </td>
                <td className="p-2.5 text-center">{r.is_active !== false
                  ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Activo</Badge>
                  : <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">Inactivo</Badge>}</td>
                <td className="p-2.5 text-right">
                  <div className="inline-flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(r)} title={r.is_system ? "Ver (sistema, no editable)" : "Editar"} data-testid={`btn-edit-rol-${r.id}`}>
                      <Edit className="w-4 h-4 text-blue-600" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => del(r)} title={r.is_system ? "Del sistema (bloqueado)" : "Eliminar"} disabled={r.is_system} data-testid={`btn-del-rol-${r.id}`}>
                      <Trash2 className={`w-4 h-4 ${r.is_system ? "text-slate-300" : "text-red-500"}`} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}{!filtered.length && <tr><td colSpan="6" className="p-8 text-center text-slate-400">Sin roles registrados</td></tr>}</tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-green-900 flex items-center gap-2">
            {editing?.is_system && <Lock className="w-4 h-4 text-slate-500" />}
            {editing ? (editing.is_system ? `Rol del sistema: ${editing.nombre}` : `Editar rol: ${editing.nombre}`) : "Nuevo rol"}
          </DialogTitle></DialogHeader>
          {editing?.is_system && (
            <div className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-md p-2">
              Este es un rol del sistema. Sus permisos son solo lectura y no se pueden modificar ni eliminar.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            <div><Label>Nombre*</Label>
              <Input value={form.nombre} onChange={e => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. Auditor Interno" disabled={editing?.is_system} data-testid="input-rol-nombre" />
            </div>
            <div><Label>Descripción</Label>
              <Textarea rows={1} value={form.descripcion} onChange={e => setForm({ ...form, descripcion: e.target.value })} placeholder="Breve descripción del rol" disabled={editing?.is_system} data-testid="input-rol-descripcion" />
            </div>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <div className="bg-green-50 text-green-900 font-semibold px-3 py-2 text-sm">Permisos por módulo</div>
            <div className="divide-y">
              {catalog.map(m => {
                const allActs = m.actions.map(a => a.action);
                const entry = form.permissions.find(p => p.module === m.module);
                const grantedCount = entry ? entry.actions.length : 0;
                const isFull = entry && allActs.every(a => entry.actions.includes(a));
                return (
                  <div key={m.module} className="p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Checkbox checked={isFull} onCheckedChange={() => !editing?.is_system && toggleModule(m.module, allActs)} disabled={editing?.is_system} data-testid={`chk-mod-${m.module}`} />
                      <span className="font-medium text-slate-800">{m.label}</span>
                      <span className="text-xs text-slate-500 ml-auto">{grantedCount} / {allActs.length}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 pl-6">
                      {m.actions.map(a => (
                        <label key={a.action} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <Checkbox checked={hasAction(m.module, a.action)} onCheckedChange={() => !editing?.is_system && toggleAction(m.module, a.action)} disabled={editing?.is_system} data-testid={`chk-${m.module}-${a.action}`} />
                          <span>{a.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cerrar</Button>
            {!editing?.is_system && <Button onClick={save} className="bg-green-700 hover:bg-green-800 text-white" data-testid="btn-guardar-rol">{editing ? "Guardar cambios" : "Crear rol"}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
